/**
 * Native push notifications (Android + iOS) for the Torik app.
 *
 * The browser builds of the SPA use Web Push (a service worker + a VAPID
 * subscription), but a Capacitor app on a phone has no service worker running
 * in the background, so the native builds register with Firebase Cloud
 * Messaging instead and the cloud delivers through FCM (see otto-web's
 * `src/server/services/fcm.ts`).
 *
 * Both transports write into the same cloud registry row — `user_devices`,
 * keyed on a `deviceKey` this module keeps per installation — so the account
 * area's "Find my device" reaches either kind of device. The row records which
 * transport the token belongs to (`pushProvider`).
 *
 * The token is posted to `POST /api/devices/push-token`; FCM re-issues a token
 * (reinstall, restore, rotation) and the `registration` listener fires again,
 * so this re-registers on every launch rather than trusting what was stored. A
 * launch that answers the listener *before* the cloud session is restored does
 * not lose that token either: it is retained and sent the moment auth appears
 * (`services/pushTokenRetry.ts`, card `t_9801b8b2`).
 */

import { watch } from 'vue';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { PushNotifications } from '@capacitor/push-notifications';
import { FCM } from '@capacitor-community/fcm';
import { cloudFetch } from './cloudApi';
import { openHubFromPush } from './pushDeepLink';
import { createPushTokenRetry } from './pushTokenRetry';
import { readPushTarget } from './pushTarget';
import { isElectron } from '../config/runtime';
import { useAuthStore } from '../stores/auth';
import { useToastStore } from '../stores/toast';

/**
 * Android notification channel. Must match ANDROID_CHANNEL_ID in otto-web's
 * `src/server/services/push.ts`, which is what FCM targets when it delivers.
 */
export const ANDROID_CHANNEL_ID = 'torik_default';

const DEVICE_KEY_STORAGE = 'torik_push_device_key';

/** True only inside the packaged app — the browser builds use Web Push. */
export function nativePushSupported(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * True when this build can hold a Web Push subscription: a browser, in a secure
 * context, with the three APIs it takes.
 *
 * Electron is excluded because it has native notifications and no push service,
 * and the packaged app is excluded because it registers with FCM instead — a
 * phone has no service worker running in the background. The API checks are not
 * ceremony: `serviceWorker` and `PushManager` are absent outright on a page that
 * is not a secure context, which is exactly the case a hub served over plain
 * `http://192.168.x.x` lands in. Silent absence would read as "push is broken".
 */
export function webPushSupported(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (nativePushSupported() || isElectron()) return false;
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** Why a subscribe attempt did not produce a subscription. */
export type WebPushRefusal =
  | 'unsupported'
  | 'not-signed-in'
  | 'not-configured'
  | 'permission-denied'
  | 'failed';

export type WebPushOutcome = { ok: true } | { ok: false; reason: WebPushRefusal };

async function readStorage(key: string): Promise<string | null> {
  try {
    const { value } = await Preferences.get({ key });
    return value ?? null;
  } catch {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }
}

async function writeStorage(key: string, value: string | null): Promise<void> {
  try {
    if (value === null) await Preferences.remove({ key });
    else await Preferences.set({ key, value });
  } catch {
    try {
      if (value === null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, value);
    } catch {
      /* storage unavailable — the key then lives for this session only */
    }
  }
}

/**
 * A stable id for this installation, so registering again updates the same
 * cloud row instead of piling up a new one per launch.
 */
export async function getOrCreatePushDeviceKey(): Promise<string> {
  const existing = await readStorage(DEVICE_KEY_STORAGE);
  if (existing) return existing;

  const key =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

  await writeStorage(DEVICE_KEY_STORAGE, key);
  return key;
}

/** "Samsung SM-T500 · Android 13" — enough to recognise the device in the list. */
function describeDevice(): { deviceName: string; platform: string } {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const platform = Capacitor.getPlatform() === 'ios' ? 'iOS' : 'Android';
  const modelMatch = ua.match(/;\s*([^;()]+)\s+Build\//);
  const model = modelMatch ? modelMatch[1].trim() : '';

  return {
    deviceName: model ? `${model} · ${platform}` : `Torik app · ${platform}`,
    platform,
  };
}

/**
 * The token the cloud can actually send to.
 *
 * Android's `registration` value IS the FCM token, so it is used as it stands.
 * iOS's is **not**: `@capacitor/push-notifications` hands over the raw APNs device
 * token (its handler is `didRegisterForRemoteNotificationsWithDeviceToken` → hex),
 * and the cloud's only sender posts to `fcm.googleapis.com/v1/…/messages:send`,
 * which has never heard of one. FCM answers an APNs token the way it answers a
 * reinstall — a "registration token" error — and `isDeadFcmToken` reads exactly
 * that as a token to FORGET, so an iPhone would quietly unsubscribe itself on the
 * first push. `@capacitor-community/fcm` is the bridge that exchanges the APNs
 * token for the FCM one; it calls `FirebaseApp.configure()` itself, so nothing in
 * `AppDelegate` has to.
 */
async function iosFcmToken(): Promise<string> {
  const { token } = await FCM.getToken();
  if (!token) throw new Error('iOS returned no FCM token for this device');
  return token;
}

/**
 * What the listeners have delivered and what the cloud has accepted — the
 * memory that outlives the cold-launch race below (card `t_9801b8b2`). The
 * policy is pure and lives in `services/pushTokenRetry.ts`.
 */
const pushTokenRetry = createPushTokenRetry();

/**
 * Hand the FCM token to the cloud. Called on every `registration` event (app
 * start and whenever FCM rotates the token) so a row the cloud has forgotten is
 * re-created rather than staying silently dead — and, since card `t_9801b8b2`,
 * called again from the auth watcher in `initNativePush` when a delivery had
 * arrived before the cloud session did.
 */
async function syncTokenWithCloud(token: string): Promise<void> {
  // Retained BEFORE the auth guard: a cold launch can deliver a token — and a
  // rotation with it — while the cloud session is still being restored, and a
  // delivery this module does not remember is a delivery the session never sees.
  pushTokenRetry.retain(token);

  const authStore = useAuthStore();
  if (!authStore.isCloudAuthenticated) return;

  const { deviceName, platform } = describeDevice();
  const deviceKey = await getOrCreatePushDeviceKey();

  await cloudFetch('/devices/push-token', {
    method: 'POST',
    body: {
      deviceKey,
      token,
      provider: 'fcm',
      deviceName,
      platform,
      deviceType: Capacitor.getPlatform() === 'ios' ? 'ios' : 'android',
    },
  });

  // The cloud took it: the retained token is settled, so the auth watcher will
  // not send it a second time. A send that threw never reaches this line, so the
  // token stays owed for the next trigger.
  pushTokenRetry.accepted(token);
}

let listenersWired = false;
let initialized = false;
/**
 * Ask for permission, register with FCM and wire the listeners. Safe to call
 * repeatedly (on every cloud sign-in): the work happens once per process and a
 * re-registration is cheap.
 */
export async function initNativePush(): Promise<void> {
  if (!nativePushSupported()) return;

  if (!listenersWired) {
    listenersWired = true;

    // Android needs the channel before FCM can post into it.
    if (Capacitor.getPlatform() === 'android') {
      try {
        await PushNotifications.createChannel({
          id: ANDROID_CHANNEL_ID,
          name: 'Torik',
          description: 'Alerts from your home and your services',
          importance: 5,
          visibility: 1,
          sound: 'default',
          vibration: true,
        });
      } catch (error) {
        console.warn('[Push] the Android notification channel could not be created:', error);
      }
    }

    await PushNotifications.addListener('registration', (token) => {
      // Fires on every launch and whenever the token rotates. On iOS the value is
      // an APNs token, so the FCM one is fetched instead — see `iosFcmToken`.
      const resolved =
        Capacitor.getPlatform() === 'ios' ? iosFcmToken() : Promise.resolve(token.value);

      resolved
        .then((value) => syncTokenWithCloud(value))
        .catch((error) => {
          console.warn('[Push] the token could not be registered with the cloud:', error);
        });
    });

    // iOS only, and the authoritative hook for this platform: the plugin's own
    // docs warn that `getToken()` can answer STALE immediately after registration,
    // where this fires when FCM actually issues or rotates the token. Android never
    // fires it — its token arrives on `registration` above — so it stays iOS-only
    // rather than registering the same device twice on Android.
    if (Capacitor.getPlatform() === 'ios') {
      await FCM.addListener('tokenReceived', (event) => {
        syncTokenWithCloud(event.token).catch((error) => {
          console.warn('[Push] the rotated token could not be registered with the cloud:', error);
        });
      });
    }

    // Card `t_9801b8b2`: a cold launch can answer `registration` — and answer a
    // ROTATED token with it — before the cloud session is restored, and
    // `syncTokenWithCloud` would then skip the send for the rest of the session.
    // Whatever a listener delivered is retained, so the moment cloud auth
    // appears the token it holds goes out. The same flag the app's own token
    // scheduler watches (`App.vue`), and the send is idempotent: a token the
    // cloud has already accepted this session is not sent again, while one whose
    // send failed still is.
    watch(
      () => useAuthStore().isCloudAuthenticated,
      (authenticated) => {
        if (!authenticated) return;
        const pending = pushTokenRetry.pending();
        if (!pending) return;
        void syncTokenWithCloud(pending).catch((error) => {
          console.warn('[Push] the retained token could not be registered with the cloud:', error);
        });
      },
    );

    await PushNotifications.addListener('registrationError', (error) => {
      console.warn('[Push] registration failed:', error);
    });

    // Foreground: the OS shows nothing, so surface it the way the app already
    // surfaces alerts.
    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      try {
        useToastStore().addToast({
          type: 'info',
          message: notification.title
            ? `${notification.title}${notification.body ? ` — ${notification.body}` : ''}`
            : notification.body || 'New alert',
          duration: 6000,
        });
      } catch (error) {
        console.warn('[Push] the foreground notification could not be shown:', error);
      }
    });

    // Tapped: connect to the hub the notification came from, then open the page
    // it was about. The payload carries `{ hubId, path }` and NOT a URL on
    // purpose — only this app can know whether the phone is on the hub's own
    // network, so the route is decided here (`services/pushDeepLink.ts`).
    await PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
      const data = event.notification?.data as Record<string, unknown> | undefined;

      const target = readPushTarget(data);
      if (target) {
        void openHubFromPush(target).catch((error) => {
          console.warn('[Push] the tapped notification could not be opened:', error);
        });
        return;
      }

      // A cloud older than the relay sent a bare `url`; follow it the way this
      // listener always did rather than dropping the tap on the floor.
      const url = typeof data?.url === 'string' ? data.url : '';
      if (url && typeof window !== 'undefined') window.location.assign(url);
    });
  }

  if (initialized) return;
  initialized = true;

  try {
    const status = await PushNotifications.checkPermissions();
    const granted = status.receive === 'granted' ? status : await PushNotifications.requestPermissions();

    if (granted.receive !== 'granted') {
      console.info('[Push] notification permission was not granted — no native push on this device');
      initialized = false; // allow a retry on the next sign-in
      return;
    }

    await PushNotifications.register();
  } catch (error) {
    initialized = false;
    console.warn('[Push] initialization failed:', error);
  }
}

/**
 * Stop this device receiving pushes: tell the cloud to forget the token (so
 * nothing is delivered to the previous account) and drop it in FCM.
 */
export async function forgetNativePush(): Promise<void> {
  if (!nativePushSupported()) return;

  try {
    const deviceKey = await getOrCreatePushDeviceKey();
    await cloudFetch('/devices/push-token', {
      method: 'POST',
      body: { deviceKey, token: null, provider: 'fcm' },
    });
  } catch (error) {
    console.warn('[Push] the cloud was not told to forget this device:', error);
  }

  try {
    await PushNotifications.unregister();
  } catch (error) {
    console.warn('[Push] could not unregister from FCM:', error);
  }
}

// ── Web Push (the browser builds) ───────────────────────────────────────────
//
// Same registry row in the cloud as the native path, with `provider: 'webpush'`
// and the Push API subscription as JSON instead of an FCM token — so the
// account's device list reaches either kind of client without caring which it
// is.

/** What this deployment can deliver, as the cloud answers. */
interface PushConfigAnswer {
  configured: boolean;
  publicKey: string;
  native: boolean;
  web: boolean;
}

/** VAPID keys travel as base64url; `PushManager` wants raw bytes. */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = window.atob(padded);
  // Backed by a plain ArrayBuffer: PushManager rejects SharedArrayBuffer-backed
  // views, and the annotation above is what tells it so.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/** "Edge on Windows" — enough to recognise the device in the account's list. */
function describeBrowser(): { deviceName: string; platform: string } {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\//.test(ua)
      ? 'Opera'
      : /SamsungBrowser/.test(ua)
        ? 'Samsung Internet'
        : /Firefox\//.test(ua) || /FxiOS/.test(ua)
          ? 'Firefox'
          : /Chrome\//.test(ua) || /CriOS/.test(ua)
            ? 'Chrome'
            : /Safari\//.test(ua)
              ? 'Safari'
              : 'Browser';
  const platform = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Macintosh|Mac OS X/.test(ua)
            ? 'macOS'
            : /CrOS/.test(ua)
              ? 'ChromeOS'
              : /Linux/.test(ua)
                ? 'Linux'
                : 'Unknown';
  return { deviceName: `${browser} on ${platform}`, platform };
}

/**
 * Subscribe this browser to Web Push and hand the subscription to the cloud.
 *
 * It deliberately does NOT register a service worker. Whatever already controls
 * this origin can hold a subscription, and which one that is depends on how the
 * app got here: embedded in the cloud's own pages, the cloud's worker owns the
 * origin (and already handles push); served standalone by a hub, it is the one
 * this build registered (`src/sw.ts`). Registering here would overwrite the
 * cloud's worker with ours in the first case — the bug `services/pwa.ts` used to
 * work around by tearing service workers down entirely.
 *
 * `askPermission` is the difference between the two callers. A permission prompt
 * with no user gesture behind it is refused or hidden by most browsers, so the
 * automatic path (a cloud sign-in) only proceeds on a permission already given,
 * and the button in the account area asks properly.
 */
export async function subscribeWebPush(
  options: { askPermission?: boolean } = {},
): Promise<WebPushOutcome> {
  if (!webPushSupported()) return { ok: false, reason: 'unsupported' };

  const authStore = useAuthStore();
  if (!authStore.isCloudAuthenticated) return { ok: false, reason: 'not-signed-in' };

  try {
    const registration = await navigator.serviceWorker.ready;

    const config = await cloudFetch<PushConfigAnswer>('/devices/push-config', { method: 'GET' });
    if (!config?.configured || !config.publicKey) return { ok: false, reason: 'not-configured' };

    if (Notification.permission !== 'granted') {
      if (!options.askPermission) return { ok: false, reason: 'permission-denied' };
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return { ok: false, reason: 'permission-denied' };
    }

    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.publicKey),
      }));

    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
      return { ok: false, reason: 'failed' };
    }

    const { deviceName, platform } = describeBrowser();
    const deviceKey = await getOrCreatePushDeviceKey();

    await cloudFetch('/devices/push-token', {
      method: 'POST',
      body: {
        deviceKey,
        token: JSON.stringify({
          endpoint: json.endpoint,
          keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        }),
        provider: 'webpush',
        deviceName,
        platform,
        deviceType: 'web',
      },
    });

    console.info('[Push] this browser is subscribed to Web Push');
    return { ok: true };
  } catch (error) {
    console.warn('[Push] the browser could not be subscribed:', error);
    return { ok: false, reason: 'failed' };
  }
}

/**
 * Stop this browser receiving pushes: tell the cloud to forget the subscription
 * — so nothing is delivered to the previous account — and drop it locally.
 *
 * The worker itself is left alone for the same reason `subscribeWebPush` does
 * not register one: it is not necessarily this app's to remove.
 */
export async function forgetWebPush(): Promise<void> {
  if (!webPushSupported()) return;

  try {
    const deviceKey = await getOrCreatePushDeviceKey();
    await cloudFetch('/devices/push-token', {
      method: 'POST',
      body: { deviceKey, token: null, provider: 'webpush' },
    });
  } catch (error) {
    console.warn('[Push] the cloud was not told to forget this browser:', error);
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    await subscription?.unsubscribe();
  } catch (error) {
    console.warn('[Push] could not drop the browser subscription:', error);
  }
}
